import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ShareIcon from "@mui/icons-material/Share";
import LinkIcon from "@mui/icons-material/Link";
import VideoSeekbar from "./VideoSeekbar";
import VideoCommentsPanel from "./VideoCommentsPanel";
import ConsoleTab from "../BugHurd/logs/ConsoleTab";
import NetworkTab from "../BugHurd/logs/NetworkTab";
import InfoTab from "../BugHurd/logs/InfoTab";
import ActionsTab from "../BugHurd/logs/ActionsTab";
import {
  useGetCommentsByBugIdQuery,
  useCreateCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
} from "@/features/bughurd/api";
import { useGetAttachmentUrlMutation } from "@/features/Global/api";
import { filterVideoComments, VideoComment } from "@/utils/videoUtils";
import PhantomLoader from "@/Components/Common/phantomLoader";
import CircularProgress from "@mui/material/CircularProgress";

interface SystemInfo {
  url?: string;
  timestamp?: string | Date;
  os?: string;
  browser?: string;
  windowSize?: string;
  screenshotDimensions?: string;
}

interface ImageItem {
  url: string;
  label?: string;
  type?: "cropped" | "fullscreen" | "attachment";
}

interface VideoPreviewPanelProps {
  videoUrl: string;
  networkLogs?: any[];
  consoleLogs?: any[];
  actionLogs?: any[];
  onClose: () => void;
  isOpen: boolean;
  bugId?: string;
  users?: Array<{ _id: string; fullName: string }>;
  bugDetailData?: any;
  systemInfo?: SystemInfo;
  isPublic?: boolean;
  publicComments?: VideoComment[];
  images?: ImageItem[];
  onAddPublicComment?: (comment: string, timestamp: number, authorName: string) => Promise<void>;
  isClosedIconVisible?: boolean;
  reactFlow?: any;
}

type PanelPosition = "right" | "bottom" | "left";

export const VideoPreviewPanel: React.FC<VideoPreviewPanelProps> = ({
  videoUrl,
  networkLogs = [],
  consoleLogs = [],
  actionLogs = [],
  onClose,
  isOpen,
  bugId,
  users = [],
  bugDetailData,
  systemInfo,
  isPublic = false,
  publicComments = [],
  images = [],
  onAddPublicComment,
  isClosedIconVisible = false,
  reactFlow,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const hasSeekToStart = useRef(false);
  const isPublicBugPage = window?.location?.pathname.includes('public/bug');

  // Theme colors based on public/private context
  const themeColors = useMemo(() => {
    if (isPublicBugPage) {
      // Light theme for public bug pages
      return {
        containerBg: "#ffffff",
        videoBg: "#f5f5f5",
        videoInnerBg: "#1a1a1a",
        tabsBg: "#f8f8f8",
        tabContentBg: "#ffffff",
        tabTextColor: "#333333",
        tabSelectedColor: "#6633d1",
        resizerBg: "#e0e0e0",
        resizerHoverBg: "#6633d1",
        resizerIndicator: "#999999",
        buttonBg: "rgba(0,0,0,0.6)",
        buttonHoverBg: "rgba(0,0,0,0.8)",
        buttonColor: "#ffffff",
        borderColor: "#e0e0e0",
        logsSectionBg: "#ffffff",
      };
    }
    // Theme-aware for regular pages
    return {
      containerBg: theme.palette.background.default,
      videoBg: theme.palette.background.default,
      videoInnerBg: "#1a1a1a",
      tabsBg: theme.palette.background.paper,
      tabContentBg: theme.palette.background.paper,
      tabTextColor: theme.palette.text.primary,
      tabSelectedColor: theme.palette.primary.main,
      resizerBg: theme.palette.divider,
      resizerHoverBg: theme.palette.primary.main,
      resizerIndicator: theme.palette.text.secondary,
      buttonBg: "rgba(0,0,0,0.5)",
      buttonHoverBg: "rgba(0,0,0,0.7)",
      buttonColor: "#ffffff",
      borderColor: theme.palette.divider,
      logsSectionBg: theme.palette.background.paper,
    };
  }, [isPublicBugPage, theme]);

  // Video Comments API hooks - skip in public mode (public comments are passed via props)
  const { data: commentsData, refetch: refetchComments } =
    useGetCommentsByBugIdQuery({ bugId: bugId || "" }, { skip: !bugId || isPublic });

  const [createComment, { isLoading: isCreatingComment }] =
    useCreateCommentMutation();
  const [updateComment] = useUpdateCommentMutation();
  const [deleteComment] = useDeleteCommentMutation();

  // Filter video comments (those with videoTimestamp)
  // Use publicComments when in public mode, otherwise fetch from API
  const videoComments: VideoComment[] = useMemo(() => {
    if (isPublic) {
      return publicComments;
    }
    const allComments = commentsData?.data?.decryptedData?.items || [];
    return filterVideoComments(allComments);
  }, [commentsData, isPublic, publicComments]);

  // State for Panel Position and Split Size
  const [panelPosition, setPanelPosition] = useState<PanelPosition>("right");
  const [splitSize, setSplitSize] = useState(60);

  // State for Comments/Tabs vertical split (Comments at top, Tabs at bottom)
  const [logsSplitSize, setLogsSplitSize] = useState(30); // Comments takes 30% initially

  // Resizing Refs
  const isDraggingRef = useRef(false);
  const isLogsDraggingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const logsSectionRef = useRef<HTMLDivElement>(null);

  // Video & Logs State
  const videoRef = useRef<HTMLVideoElement>(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);

  // Image gallery state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [resolvedImageUrls, setResolvedImageUrls] = useState<Record<number, string>>({});
  const [imageLoadingStates, setImageLoadingStates] = useState<Record<number, boolean>>({});
  const processedImagesRef = useRef<Set<number>>(new Set());
  const hasImages = images && images.length > 0;
  const hasVideo = !!videoUrl;

  // API mutation for fetching S3 URLs
  const [getAttachmentUrl] = useGetAttachmentUrlMutation();

  // Fetch S3 URLs for images that need it
  useEffect(() => {
    if (!hasImages) return;

    // Reset processed images if images array changes
    if (images.length !== processedImagesRef.current.size) {
      processedImagesRef.current = new Set();
    }

    images.forEach((image, index) => {
      // Skip if already processed
      if (processedImagesRef.current.has(index)) return;
      processedImagesRef.current.add(index);

      // Case 1: Already a public URL (https://) or base64
      if (image.url?.startsWith("https://") || image.url?.startsWith("data:")) {
        setResolvedImageUrls((prev) => ({ ...prev, [index]: image.url }));
        return;
      }

      // Case 2: Need to fetch from S3
      if (image.url) {
        setImageLoadingStates((prev) => ({ ...prev, [index]: true }));

        getAttachmentUrl({ key: image.url })
          .unwrap()
          .then((res) => {
            if (res?.data?.decryptedData?.url) {
              setResolvedImageUrls((prev) => ({
                ...prev,
                [index]: res.data.decryptedData.url,
              }));
            }
          })
          .catch((err) => {
            console.error("Error fetching image URL:", err);
          })
          .finally(() => {
            setImageLoadingStates((prev) => ({ ...prev, [index]: false }));
          });
      }
    });
  }, [images, hasImages, getAttachmentUrl]);

  // 1. Calculate Recording Start Time (convert all timestamps to ms)
  const recordingStartTime = useMemo(() => {
    let earliest = Infinity;
    if (networkLogs && networkLogs.length > 0) {
      networkLogs.forEach((log) => {
        // Network logs have startTime in ms, or timestamp which could be number or ISO string
        let t = log.startTime;
        if (!t && log.timestamp) {
          t = typeof log.timestamp === 'number'
            ? log.timestamp
            : new Date(log.timestamp).getTime();
        }
        if (t && !isNaN(t) && t < earliest) earliest = t;
      });
    }
    if (consoleLogs && consoleLogs.length > 0) {
      consoleLogs.forEach((log) => {
        // Console logs have timestamp as ISO string or number
        const t = typeof log.timestamp === 'number'
          ? log.timestamp
          : new Date(log.timestamp).getTime();
        if (!isNaN(t) && t < earliest) earliest = t;
      });
    }
    if (actionLogs && actionLogs.length > 0) {
      actionLogs.forEach((log) => {
        // Action logs have timestamp as number (ms)
        const t = log.timestamp;
        if (t && !isNaN(t) && t < earliest) earliest = t;
      });
    }
    return earliest === Infinity ? 0 : earliest;
  }, [networkLogs, consoleLogs, actionLogs]);

  // 2. Resizing Logic
  const isHorizontal = panelPosition === "right" || panelPosition === "left";

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    document.body.style.cursor = isHorizontal ? "col-resize" : "row-resize";
    document.body.style.userSelect = "none";
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      let newPercent = 60;

      if (isHorizontal) {
        const relativeX = e.clientX - containerRect.left;
        newPercent = (relativeX / containerRect.width) * 100;
      } else {
        const relativeY = e.clientY - containerRect.top;
        newPercent = (relativeY / containerRect.height) * 100;
      }
      setSplitSize(Math.max(20, Math.min(80, newPercent)));
    };

    const handleMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isHorizontal]);

  // 3. Video Logic (Safe & Mounted)
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    let isMounted = true;

    const handleLoadedMetadata = () => {
      if (!isMounted) return;
      setVideoDuration(videoElement.duration || 0);
      setVideoLoading(false);
      setVideoError(false);
    };

    const handleCanPlay = () => {
      if (!isMounted) return;
      setVideoLoading(false);
      setVideoError(false);
      if (videoElement.duration) setVideoDuration(videoElement.duration);

      // Seek to startFrom timestamp if present in URL (only once)
      if (!hasSeekToStart.current) {
        const startFrom = searchParams.get("startFrom");
        if (startFrom) {
          const startTime = parseFloat(startFrom);
          if (!isNaN(startTime) && startTime >= 0 && startTime <= videoElement.duration) {
            videoElement.currentTime = startTime;
            hasSeekToStart.current = true;
          }
        }
      }
    };

    const handleTimeUpdate = () => {
      if (!isMounted) return;
      const currentTime = videoElement.currentTime || 0;
      setCurrentVideoTime(currentTime);
    };

    const handleError = () => {
      if (!isMounted) return;
      setVideoLoading(false);
      setVideoError(true);
    };

    videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);
    videoElement.addEventListener("timeupdate", handleTimeUpdate);
    videoElement.addEventListener("canplay", handleCanPlay);
    videoElement.addEventListener("error", handleError);

    if (videoElement.readyState >= 3) {
      handleCanPlay();
    }

    return () => {
      isMounted = false;
      if (videoElement) {
        videoElement.removeEventListener(
          "loadedmetadata",
          handleLoadedMetadata
        );
        videoElement.removeEventListener("timeupdate", handleTimeUpdate);
        videoElement.removeEventListener("canplay", handleCanPlay);
        videoElement.removeEventListener("error", handleError);
      }
    };
  }, [videoUrl, networkLogs, recordingStartTime]);

  // 4. Video Comment Handlers
  const handleAddVideoComment = async (comment: string, timestamp: number) => {
    // Disable in public mode
    if (isPublic) {
      toast.info(t("Comments are read-only in public view"));
      return;
    }

    if (!bugId) {
      toast.error(t("Bug ID is required to add comments"));
      return;
    }

    try {
      await createComment({
        comment,
        itemId: bugId,
        module: "BUG_TRACKING",
        type: "PUBLIC",
        videoTimestamp: timestamp,
        isComment: true,
        mentionedUsers: [],
        jiraObj: bugDetailData?.jiraConfig?.projectKey
          ? { itemkey: bugDetailData?.jiraKey }
          : undefined,
      }).unwrap();

      toast.success(t("Video comment added successfully"));
      refetchComments();
    } catch (error) {
      console.error("Error adding video comment:", error);
      toast.error(t("Failed to add video comment"));
    }
  };

  const handleEditVideoComment = async (
    commentId: string,
    newText: string
  ) => {
    // Disable in public mode
    if (isPublic) return;
    if (!bugId) return;

    try {
      await updateComment({
        comment: newText,
        itemId: bugId,
        commentId,
      }).unwrap();

      toast.success(t("Comment updated successfully"));
      refetchComments();
    } catch (error) {
      console.error("Error updating comment:", error);
      toast.error(t("Failed to update comment"));
    }
  };

  const handleDeleteVideoComment = async (commentId: string) => {
    // Disable in public mode
    if (isPublic) return;

    try {
      await deleteComment({ commentId }).unwrap();
      toast.success(t("Comment deleted successfully"));
      refetchComments();
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error(t("Failed to delete comment"));
    }
  };

  const handleSeekToTimestamp = (timestamp: number) => {
    if (videoRef.current) {
      // Clamp the timestamp within valid video bounds
      const duration = videoRef.current.duration || 0;
      const clampedTime = Math.max(0, Math.min(timestamp, duration));
      videoRef.current.currentTime = clampedTime;
    }
  };

  const handleFailedApiClick = (timestamp: number, _logIds: string[]) => {
    // Seek to the timestamp
    if (videoRef.current) {
      videoRef.current.currentTime = timestamp;
    }
    // Switch to Network tab (index 2)
    setTabIndex(2);
  };

  const handleShareVideo = async () => {
    const currentTime = videoRef.current?.currentTime || 0;
    const formattedTime = currentTime.toFixed(2);

    // Build the share URL with startFrom parameter
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("startFrom", formattedTime);

    try {
      await navigator.clipboard.writeText(currentUrl.toString());
      toast.success(
        t("Link copied! Video will start at {{time}}", {
          time: formatTimestamp(currentTime),
        })
      );
    } catch (error) {
      console.error("Failed to copy link:", error);
      toast.error(t("Failed to copy link"));
    }
  };

  // Copy public link for sharing without authentication
  const handleCopyPublicLink = async () => {
    if (!bugId) {
      toast.error(t("Bug ID is required to generate public link"));
      return;
    }

    const publicUrl = `${window.location.origin}/public/bug/${bugId}`;

    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success(t("Public link copied!"));
    } catch (error) {
      console.error("Failed to copy public link:", error);
      toast.error(t("Failed to copy public link"));
    }
  };

  // Helper to format timestamp for display (MM:SS)
  const formatTimestamp = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // --- JSX RENDERERS (Defined as variables to avoid re-mount issues) ---
  // Allow rendering if there's a video OR images
  if (!isOpen || (!hasVideo && !hasImages)) return null;
  const renderVideoSection = () => (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        bgcolor: themeColors.videoBg,
        position: "relative",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: 1,
          position: "relative",
          bgcolor: themeColors.videoInnerBg,
          display: "flex",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {videoLoading && (
          <Box sx={{ position: "absolute", zIndex: 10, top: "25%" }}>
            <PhantomLoader />
          </Box>
        )}
        {videoError && (
          <Box
            sx={{
              position: "absolute",
              zIndex: 10,
              color: theme.palette.error.main,
              top: "50%",
            }}
          >
            <Typography variant="body2">{t("Error loading video")}</Typography>
          </Box>
        )}

        <video
          ref={videoRef}
          src={videoUrl}
          autoPlay
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: videoError ? "none" : "block",
          }}
          onLoadStart={() => {
            setVideoLoading(true);
            setVideoError(false);
          }}
          onClick={() => {
            // Toggle play/pause on video click
            if (videoRef.current) {
              if (videoRef.current.paused) {
                videoRef.current.play();
              } else {
                videoRef.current.pause();
              }
            }
          }}
        />

        {/* Custom Video Seekbar with Timeline Events Container for Comments & Failed APIs */}
        <VideoSeekbar
          videoRef={videoRef}
          currentTime={currentVideoTime}
          duration={videoDuration}
          comments={videoComments}
          networkLogs={networkLogs}
          recordingStartTime={recordingStartTime}
          onSeek={handleSeekToTimestamp}
          onCommentClick={handleSeekToTimestamp}
          onFailedApiClick={handleFailedApiClick}
        />

        {/* Top Right Buttons - Share, Public Link & Close */}
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            display: "flex",
            gap: 1,
          }}
        >
          <Tooltip title={t("Share video at current time")}>
            <IconButton
              size="small"
              onClick={handleShareVideo}
              sx={{
                color: themeColors.buttonColor,
                backgroundColor: themeColors.buttonBg,
                "&:hover": {
                  backgroundColor: themeColors.buttonHoverBg,
                },
              }}
            >
              <ShareIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {/* {bugId && (
            <Tooltip title={t("Copy public link")}>
              <IconButton
                size="small"
                onClick={handleCopyPublicLink}
                sx={{
                  color: themeColors.buttonColor,
                  backgroundColor: themeColors.buttonBg,
                  "&:hover": {
                    backgroundColor: themeColors.buttonHoverBg,
                  },
                }}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )} */}
          {(!isClosedIconVisible && !isPublic) && <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: themeColors.buttonColor,
              backgroundColor: themeColors.buttonBg,
              "&:hover": {
                backgroundColor: themeColors.buttonHoverBg,
              },
            }}
          >
            <CloseIcon />
          </IconButton>}
        </Box>
      </Box>
    </Box>
  );

  // Render image gallery section when there's no video but has images
  const renderImageSection = () => {
    const currentImage = images[currentImageIndex];
    const currentResolvedUrl = resolvedImageUrls[currentImageIndex];
    const isCurrentImageLoading = imageLoadingStates[currentImageIndex];

    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          bgcolor: themeColors.videoBg,
          position: "relative",
          height: "100%",
          overflow: "hidden",
        }}
      >
        {/* Main Image Display */}
        <Box
          sx={{
            flex: 1,
            position: "relative",
            bgcolor: themeColors.videoInnerBg,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          {isCurrentImageLoading && (
            <Box sx={{ position: "absolute", zIndex: 10 }}>
              <CircularProgress size={40} sx={{ color: "#fff" }} />
            </Box>
          )}
          {currentImage && currentResolvedUrl && !isCurrentImageLoading && (
            <Box
              component="img"
              src={currentResolvedUrl}
              alt={currentImage.label || `Image ${currentImageIndex + 1}`}
              sx={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          )}
          {currentImage && !currentResolvedUrl && !isCurrentImageLoading && (
            <Typography variant="body2" sx={{ color: "#fff" }}>
              {t("Failed to load image")}
            </Typography>
          )}

          {/* Top Right Buttons - Public Link & Close */}
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              display: "flex",
              gap: 1,
            }}
          >
            {/* {bugId && (
              <Tooltip title={t("Copy public link")}>
                <IconButton
                  size="small"
                  onClick={handleCopyPublicLink}
                  sx={{
                    color: themeColors.buttonColor,
                    backgroundColor: themeColors.buttonBg,
                    "&:hover": {
                      backgroundColor: themeColors.buttonHoverBg,
                    },
                  }}
                >
                  <LinkIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )} */}
            {(!isClosedIconVisible && !isPublic) && (
              <IconButton
                size="small"
                onClick={onClose}
                sx={{
                  color: themeColors.buttonColor,
                  backgroundColor: themeColors.buttonBg,
                  "&:hover": {
                    backgroundColor: themeColors.buttonHoverBg,
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Thumbnail Strip - Only show if multiple images */}
        {images.length > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
              p: 1.5,
              bgcolor: isPublicBugPage ? "#ffffff" : theme.palette.background.paper,
              borderTop: `1px solid ${themeColors.borderColor}`,
            }}
          >
            {images.map((image, index) => {
              const thumbnailUrl = resolvedImageUrls[index];
              const isThumbnailLoading = imageLoadingStates[index];

              return (
                <Box
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  sx={{
                    width: 60,
                    height: 45,
                    borderRadius: 1,
                    overflow: "hidden",
                    cursor: "pointer",
                    border: currentImageIndex === index
                      ? `2px solid ${themeColors.tabSelectedColor}`
                      : `2px solid transparent`,
                    opacity: currentImageIndex === index ? 1 : 0.7,
                    transition: "all 0.2s ease",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    bgcolor: "#1a1a1a",
                    "&:hover": {
                      opacity: 1,
                    },
                  }}
                >
                  {isThumbnailLoading ? (
                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                  ) : thumbnailUrl ? (
                    <Box
                      component="img"
                      src={thumbnailUrl}
                      alt={image.label || `Thumbnail ${index + 1}`}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : null}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Image Labels - Show below thumbnails */}
        {images.length > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 1,
              pb: 1,
              bgcolor: isPublicBugPage ? "#ffffff" : theme.palette.background.paper,
            }}
          >
            {images.map((image, index) => (
              <Typography
                key={index}
                variant="caption"
                onClick={() => setCurrentImageIndex(index)}
                sx={{
                  cursor: "pointer",
                  px: 1,
                  py: 0.25,
                  borderRadius: 0.5,
                  color: currentImageIndex === index
                    ? themeColors.tabSelectedColor
                    : themeColors.tabTextColor,
                  fontWeight: currentImageIndex === index ? 600 : 400,
                  borderBottom: currentImageIndex === index
                    ? `2px solid ${themeColors.tabSelectedColor}`
                    : "2px solid transparent",
                  "&:hover": {
                    color: themeColors.tabSelectedColor,
                  },
                }}
              >
                {image.label || (image.type === "cropped" ? "Cropped" : image.type === "fullscreen" ? "Full screen" : `Image ${index + 1}`)}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  // Render the appropriate media section (video or images)
  const renderMediaSection = () => {
    if (hasVideo) {
      return renderVideoSection();
    }
    if (hasImages) {
      return renderImageSection();
    }
    return null;
  };

  const renderLogsSection = () => {
    // Handle vertical resizing for Comments/Tabs split
    const handleLogsMouseDown = (e: React.MouseEvent) => {
      e.preventDefault();
      isLogsDraggingRef.current = true;
      document.body.style.cursor = "row-resize";
      document.body.style.userSelect = "none";

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isLogsDraggingRef.current || !logsSectionRef.current) return;

        const rect = logsSectionRef.current.getBoundingClientRect();
        const newSize = ((moveEvent.clientY - rect.top) / rect.height) * 100;
        const clampedSize = Math.min(Math.max(newSize, 15), 85);
        setLogsSplitSize(clampedSize);
      };

      const handleMouseUp = () => {
        isLogsDraggingRef.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };

    return (
      <Box
        ref={logsSectionRef}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
          bgcolor: themeColors.logsSectionBg,
        }}
      >
        {/* Comments Section - Fixed at Top */}
        {bugId && (
          <>
            <Box
              sx={{
                flexBasis: `${logsSplitSize}%`,
                flexGrow: 0,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                borderBottom: `1px solid ${themeColors.borderColor}`,
              }}
            >
              {/* <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: `1px solid ${themeColors.borderColor}`,
                  bgcolor: themeColors.tabsBg,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: themeColors.tabTextColor,
                  }}
                >
                  {t("Comments")} ({videoComments.length})
                </Typography>
              </Box> */}
              <Box sx={{ flex: 1, overflow: "auto" }}>
                <VideoCommentsPanel
                  comments={videoComments}
                  currentVideoTime={currentVideoTime}
                  onCommentClick={handleSeekToTimestamp}
                  onAddComment={handleAddVideoComment}
                  onAddPublicComment={onAddPublicComment}
                  onEditComment={handleEditVideoComment}
                  onDeleteComment={handleDeleteVideoComment}
                  isLoading={isCreatingComment}
                  users={users}
                  isPublic={isPublic}
                  isImageMode={!hasVideo && hasImages}
                />
              </Box>
            </Box>

            {/* Horizontal Resizer */}
            <Box
              onMouseDown={handleLogsMouseDown}
              sx={{
                height: "1px",
                cursor: "row-resize",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: themeColors.resizerBg,
                transition: "background-color 0.2s ease",
                "&:hover": {
                  bgcolor: themeColors.resizerHoverBg,
                },
                "&:hover .resizer-indicator": {
                  opacity: 1,
                },
              }}
            >
              <Box
                className="resizer-indicator"
                sx={{
                  width: "40px",
                  height: "3px",
                  borderRadius: "2px",
                  bgcolor: themeColors.resizerIndicator,
                  opacity: 0.5,
                  transition: "opacity 0.2s ease",
                }}
              />
            </Box>
          </>
        )}

        {/* Tabs Section - Info only for images, all tabs for video */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: `1px solid ${themeColors.borderColor}`,
              bgcolor: themeColors.tabsBg,
            }}
          >
            <Tabs
              value={tabIndex}
              onChange={(_, newValue) => setTabIndex(newValue)}
              sx={{
                minHeight: 40,
                "& .MuiTab-root": {
                  minHeight: 40,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  color: themeColors.tabTextColor,
                  bgcolor: themeColors.tabsBg,
                },
                "& .Mui-selected": {
                  color: `${themeColors.tabSelectedColor} !important`,
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: themeColors.tabSelectedColor,
                },
              }}
            >
              <Tab label="Info" />
              {/* Only show Console, Network, Actions tabs for video */}
              {hasVideo && <Tab label={`Console (${consoleLogs.length})`} />}
              {hasVideo && <Tab label={`Network (${networkLogs.length})`} />}
              {hasVideo && <Tab label={`Actions (${actionLogs.length})`} />}
            </Tabs>
          </Box>

          <Box sx={{ flex: 1, overflow: "hidden", position: "relative", bgcolor: themeColors.tabContentBg }}>
            {/* Info Tab */}
            {tabIndex === 0 && (
              <Box sx={{ height: "100%", overflow: "auto" }}>
                <InfoTab
                  url={systemInfo?.url}
                  timestamp={systemInfo?.timestamp}
                  os={systemInfo?.os}
                  browser={systemInfo?.browser}
                  windowSize={systemInfo?.windowSize}
                  screenshotDimensions={systemInfo?.screenshotDimensions}
                />
              </Box>
            )}

            {/* Console Tab - Only for video */}
            {hasVideo && tabIndex === 1 && (
              <Box sx={{ height: "100%", overflow: "auto" }}>
                <ConsoleTab
                  devToolsData={consoleLogs}
                  selectedTab="console"
                  currentVideoTime={currentVideoTime}
                  recordingStartTime={recordingStartTime}
                  isFromVideoPreview={true}
                  onSeekToTime={handleSeekToTimestamp}
                />
              </Box>
            )}

            {/* Network Tab - Only for video */}
            {hasVideo && tabIndex === 2 && (
              <Box sx={{ height: "100%", overflow: "auto" }}>
                <NetworkTab
                  networkRequests={networkLogs}
                  selectedTab="network"
                  isFromVideoPreview={true}
                  currentVideoTime={currentVideoTime}
                  recordingStartTime={recordingStartTime}
                  onSeekToTime={handleSeekToTimestamp}
                  reactFlow={reactFlow}
                />
              </Box>
            )}

            {/* Actions Tab - Only for video */}
            {hasVideo && tabIndex === 3 && (
              <Box sx={{ height: "100%", overflow: "auto" }}>
                <ActionsTab
                  actionLogs={actionLogs}
                  recordingStartTime={recordingStartTime}
                  onSeekToTime={handleSeekToTimestamp}
                  bugCreatedAt={bugDetailData?.createdAt}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        flexDirection: isHorizontal ? "row" : "column",
        height: "100%",
        width: "100%",
        gap: 0,
        bgcolor: themeColors.containerBg,
        overflow: "hidden",
      }}
    >
      {/* Pane 1 */}
      <Box
        sx={{
          flexBasis: `${splitSize}%`,
          flexGrow: 0,
          flexShrink: 0,
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {panelPosition === "left" ? renderLogsSection() : renderMediaSection()}
      </Box>

      {/* Resizer */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          flexBasis: "6px",
          cursor: isHorizontal ? "col-resize" : "row-resize",
          bgcolor: themeColors.resizerBg,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 100,
          "&:hover": { bgcolor: themeColors.resizerHoverBg },
          transition: "background-color 0.2s",
        }}
      >
        <Box
          sx={{
            width: isHorizontal ? "2px" : "30px",
            height: isHorizontal ? "30px" : "2px",
            bgcolor: themeColors.resizerIndicator,
            borderRadius: 1,
          }}
        />
      </Box>

      {/* Pane 2 */}
      <Box
        sx={{
          flex: 1,
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          minHeight: 0,
        }}
      >
        {panelPosition === "left" ? renderMediaSection() : renderLogsSection()}
      </Box>
    </Box>
  );
};

export default VideoPreviewPanel;
